/**
 * A lista "Aguardando entrega" (história 6.5) — as vendas fechadas cuja carga ninguém registrou.
 *
 * Ela existe porque a 6.4 sozinha não funcionaria. O Negócio vira ganho **automaticamente** quando
 * o Pedido é liberado no ERP (5.4), e a entrega acontece dias ou semanas depois: sem uma tela que
 * mostre o que está pendente, o campo `entregue_em` nasceria vazio e continuaria vazio, e a
 * indicação de garantia do FR-53 nunca acenderia. O risco que esta tela cobre não é técnico — é o
 * de a funcionalidade existir e ninguém usar.
 *
 * **A lista encolhendo é o produto.** Preenchida a data, a linha sai na próxima leitura: quem abre
 * a tela e vê pouco sabe que a operação está em dia, e é por isso que o estado vazio aqui é uma boa
 * notícia, e não uma tela quebrada.
 *
 * **A gravação é a mesma da ficha**, e não uma segunda: `useRegistrarEntrega` chama
 * `PUT /negocios/{id}/entrega`, da 6.4. Uma segunda porta de escrita teria uma segunda chance de
 * esquecer a trilha de auditoria — e é a trilha que sustenta a data que decide garantia.
 *
 * **A visibilidade é do serviço, e esta tela não decide nada sobre ela.** As linhas já chegam
 * recortadas pela RLS: o Vendedor recebe a Carteira dele, o Gestor recebe tudo (FR-30). Um `filter`
 * aqui seria a mesma regra escrita num segundo lugar, e os dois divergiriam no primeiro ajuste.
 *
 * **Não há campo de dinheiro, e a ausência não é recorte** (FR-62): o contrato do serviço não tem
 * `valor` para perfil nenhum. Não há três estados a distinguir nem "valor não visível para SDR" a
 * escrever — não há coluna.
 *
 * **O nome da Empresa é resolvido aqui, em lote** (AD-25), como na lista de Originados — nunca uma
 * consulta por linha. Nome que não veio vira o **código** do Negócio, que é o identificador que a
 * operação já usa na boca (AD-12).
 *
 * **Cada linha tem o próprio estado de gravação**, e por isso cada uma é um componente: duas
 * pessoas registrando duas entregas na mesma tela não disputam um `isPending` só, e o erro de uma
 * linha não apaga a outra.
 *
 * **A tabela é TanStack Table 9 (AD-29, 11.4)** — a primeira do SPA: headless, com ordenação por
 * clique no cabeçalho (`aria-sort` no `<th>`). No celular ela vira a lista de cartões-resumo, com a
 * mesma função (NFR-1).
 *
 * **A gravação confirma num aviso** (pedido do Gustavo, 28/09/2026): a linha some da lista na
 * releitura, e sem "✅ Pedido dado como entregue" a pessoa não sabe se gravou ou se a tela só
 * piscou. O aviso vai pela região do layout (`anunciar`, a mesma da entrega do Negócio na 11.5),
 * que sobrevive à linha desmontada — por isso ele sai do `mutateAsync`, e não de um `onSuccess`
 * do `mutate`, que o TanStack silencia quando o componente já saiu da árvore.
 */

import {
  type SortingState,
  createColumnHelper,
  createSortedRowModel,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_text,
  tableFeatures,
  useTable,
} from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useNomesDeEmpresas } from '@/modulos/cadastro/api'
import {
  type NegocioAguardandoEntrega,
  useAguardandoEntrega,
  useRegistrarEntrega,
} from '@/modulos/negocios/api'
import { DATA, hojeEmSaoPaulo } from '@/modulos/negocios/apresentacao'
import { anunciar } from '@/modulos/negocios/avisos'
import { useEhCelular } from '@/shell/Shell'
import './aguardando-entrega.css'

/**
 * O nome da lista — é o `<caption>` da tabela (que a nomeia para o leitor de tela) e o
 * `aria-label` da lista do celular. Exportado porque a suíte a encontra por ele.
 */
export const NOME_DA_LISTA = 'Negócios aguardando entrega'
/** O cartão da tela, com as palavras do protótipo v2. */
export const TITULO_DO_CARTAO = 'Negócios ganhos sem data de entrega'
export const NOTA_DO_CARTAO = 'preencher a data acende a garantia na RAT'
const CARREGANDO = 'Carregando os negócios…'

/** O vazio é boa notícia, e o texto diz isso — não é uma tabela em branco. */
export const SEM_PENDENCIA =
  'Nenhum negócio ganho aguardando data. Marque um como ganho no Funil para ver aqui.'

const TENTAR_DE_NOVO = 'Tentar de novo'
/** O aviso da gravação — com o check, como o Gustavo pediu. */
export const PEDIDO_ENTREGUE = '✅ Pedido dado como entregue'
const NOMES_NAO_VIERAM = 'Não foi possível carregar os nomes das empresas; os códigos continuam.'
const SEM_PEDIDO = '—'

const ROTULOS = {
  codigo: 'Código',
  empresa: 'Empresa',
  pedido: 'Nº do Pedido',
  ganho: 'Ganho em',
  entrega: 'Entregue em',
} as const

/** A ajuda do campo, escrita uma vez: ela é a pergunta que a tela faz. */
const AJUDA =
  'Informe o dia em que o cliente recebeu ou retirou o produto. É essa data que conta a garantia.'

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/*
 * O piso do campo de data. Não é regra de negócio — o serviço não recusa data antiga —, é anteparo
 * de digitação: sem ele, teclar o ano dígito a dígito passa por `0002` e `0202`, que o `max` deixa
 * passar por serem bem menores que hoje.
 */
const PISO_DA_ENTREGA = '2000-01-01'

/** Uma linha da lista, já resolvida para a tela. */
interface LinhaDaEntrega {
  id: string
  titulo: string
  codigo: string
  /** O código como número, para a ordenação numérica da coluna. */
  codigoNumerico: number
  pedido: string
  /** O Pedido para ordenar — `undefined` quando não há, e aí a linha vai sempre ao fim. */
  pedidoOrdem: string | undefined
  ganho: string
  /**
   * A data do ganho em ISO, para ordenar por instante e não pelo texto formatado — `undefined`
   * quando não há, e aí a linha vai sempre ao fim (`sortUndefined: 'last'`).
   */
  ganhoOrdem: string | undefined
}

/** As capacidades da tabela, registradas uma vez no módulo (v9: só o que se usa). */
const recursos = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric, text: sortFn_text },
})

const coluna = createColumnHelper<typeof recursos, LinhaDaEntrega>()

/** As colunas da 6.5, na mesma ordem. A de entrega é o campo de gravação — não ordena. */
const COLUNAS = coluna.columns([
  coluna.accessor('codigoNumerico', {
    id: 'codigo',
    header: ROTULOS.codigo,
    cell: (contexto) => contexto.row.original.codigo,
  }),
  coluna.accessor('titulo', { id: 'empresa', header: ROTULOS.empresa, sortFn: 'text' }),
  coluna.accessor('pedidoOrdem', {
    id: 'pedido',
    header: ROTULOS.pedido,
    sortFn: 'alphanumeric',
    sortUndefined: 'last',
    cell: (contexto) => contexto.row.original.pedido,
  }),
  coluna.accessor('ganhoOrdem', {
    id: 'ganho',
    header: ROTULOS.ganho,
    sortFn: 'alphanumeric',
    sortUndefined: 'last',
    cell: (contexto) => contexto.row.original.ganho,
  }),
  coluna.display({
    id: 'entrega',
    header: ROTULOS.entrega,
    enableSorting: false,
    cell: (contexto) => (
      <CampoDaEntrega negocioId={contexto.row.original.id} codigo={contexto.row.original.codigo} />
    ),
  }),
])

/** O `aria-sort` do cabeçalho a partir do estado da coluna. */
function ariaSort(direcao: false | 'asc' | 'desc'): 'ascending' | 'descending' | 'none' {
  return direcao === 'asc' ? 'ascending' : direcao === 'desc' ? 'descending' : 'none'
}

/** A tabela do computador — ordenável pelo cabeçalho. */
function TabelaDasEntregas({ linhas }: { linhas: LinhaDaEntrega[] }) {
  const [ordem, setOrdem] = useState<SortingState>([])
  const tabela = useTable({
    features: recursos,
    columns: COLUNAS,
    data: linhas,
    state: { sorting: ordem },
    onSortingChange: setOrdem,
    getRowId: (linha) => linha.id,
    // Todo cabeçalho começa em crescente — o código e a data não viram "do maior" sozinhos.
    sortDescFirst: false,
  })

  return (
    <div className="entregas__rolagem">
      <table className="entregas__tabela">
        <caption className="entregas__legenda">{NOME_DA_LISTA}</caption>
        <thead>
          {tabela.getHeaderGroups().map((grupo) => (
            <tr key={grupo.id}>
              {grupo.headers.map((cabecalho) => {
                const ordena = cabecalho.column.getCanSort()
                const direcao = cabecalho.column.getIsSorted()
                return (
                  <th
                    key={cabecalho.id}
                    scope="col"
                    aria-sort={ordena ? ariaSort(direcao) : undefined}
                    data-coluna={cabecalho.column.id}
                  >
                    {ordena ? (
                      <button
                        type="button"
                        className="entregas__ordenar"
                        onClick={cabecalho.column.getToggleSortingHandler()}
                      >
                        <tabela.FlexRender header={cabecalho} />
                        <span className="entregas__seta" aria-hidden="true">
                          {direcao === 'asc' ? '▲' : direcao === 'desc' ? '▼' : ''}
                        </span>
                      </button>
                    ) : (
                      <tabela.FlexRender header={cabecalho} />
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {tabela.getRowModel().rows.map((linha) => (
            <tr key={linha.id}>
              {linha.getAllCells().map((celula) => (
                <td
                  key={celula.id}
                  className={
                    celula.column.id === 'codigo'
                      ? 'entregas__codigo'
                      : celula.column.id === 'empresa'
                        ? 'entregas__empresa'
                        : undefined
                  }
                >
                  <tabela.FlexRender cell={celula} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * O campo de data de uma linha — com o estado de gravação **dela**, e não da tela.
 *
 * Grava no `onChange`, como o campo da ficha: o controle é um só, o ato é inequívoco, e um botão
 * "Salvar" por linha pediria dois gestos para um dado que a pessoa já terminou de informar. O
 * campo vazio não dispara nada — apagar a data não é um ato que o serviço tenha.
 */
function CampoDaEntrega({ negocioId, codigo }: { negocioId: string; codigo: string }) {
  const registro = useRegistrarEntrega(negocioId)
  /*
   * Estado local, e a gravação só no `blur` — a mesma correção da ficha, pela mesma razão: no
   * navegador, digitar o ano produz datas completas intermediárias (`0002-…`, `0202-…`), e cada
   * uma seria um `PUT` aceito e uma linha permanente na trilha. Ver `LinhaDaEntrega`.
   */
  const [rascunho, setRascunho] = useState('')
  return (
    <>
      <input
        className="entregas__campo"
        type="date"
        min={PISO_DA_ENTREGA}
        max={hojeEmSaoPaulo()}
        disabled={registro.isPending}
        /* O **código** do Negócio, e não o uuid: é o identificador que a operação usa na boca, e
           era o uuid inteiro que o leitor de tela recitava. */
        aria-label={`${ROTULOS.entrega} — negócio ${codigo}`}
        value={rascunho}
        onChange={(evento) => {
          setRascunho(evento.target.value)
        }}
        onBlur={() => {
          if (rascunho === '') {
            return
          }
          /* A recusa fica no `registro.isError` (o `alert` abaixo); a promessa só serve ao aviso. */
          registro.mutateAsync(rascunho).then(
            () => {
              anunciar(PEDIDO_ENTREGUE)
            },
            () => undefined,
          )
        }}
      />
      {registro.isError ? (
        <span className="entregas__erro" role="alert">
          {mensagemDe(registro.error)}
        </span>
      ) : null}
    </>
  )
}

function CartaoDaEntrega({ linha }: { linha: LinhaDaEntrega }) {
  return (
    <li className="entregas__cartao">
      <p className="entregas__titulo">{linha.titulo}</p>
      <dl className="entregas__pares">
        <div className="entregas__par">
          <dt>{ROTULOS.codigo}</dt>
          <dd>{linha.codigo}</dd>
        </div>
        <div className="entregas__par">
          <dt>{ROTULOS.pedido}</dt>
          <dd>{linha.pedido}</dd>
        </div>
        <div className="entregas__par">
          <dt>{ROTULOS.ganho}</dt>
          <dd>{linha.ganho}</dd>
        </div>
      </dl>
      <label className="entregas__rotulo">
        {ROTULOS.entrega}
        <CampoDaEntrega negocioId={linha.id} codigo={linha.codigo} />
      </label>
    </li>
  )
}

export function AguardandoEntrega() {
  const ehCelular = useEhCelular()
  const consulta = useAguardandoEntrega()

  const itens = useMemo<NegocioAguardandoEntrega[]>(() => consulta.data ?? [], [consulta.data])

  /* Um pedido em lote para a tela inteira, e não um por linha (AD-25). */
  const idsDeEmpresas = useMemo(
    () => [...new Set(itens.flatMap((item) => (item.empresa_id === null ? [] : [item.empresa_id])))],
    [itens],
  )
  const empresas = useNomesDeEmpresas(idsDeEmpresas)

  const linhas: LinhaDaEntrega[] = useMemo(
    () =>
      itens.map((item) => ({
        id: item.id,
        /* Nome que não veio vira o código — o identificador que a operação já usa (AD-12). */
        titulo:
          (item.empresa_id === null ? undefined : empresas.nomes.get(item.empresa_id)) ??
          `Negócio ${String(item.codigo)}`,
        codigo: String(item.codigo),
        codigoNumerico: item.codigo,
        /* O travessão é o Negócio ganho **à mão** (FR-73): ele não tem Pedido, e vem na lista. */
        pedido: item.numero_do_pedido ?? SEM_PEDIDO,
        ganho:
          item.data_de_conclusao === null
            ? SEM_PEDIDO
            : DATA.format(new Date(item.data_de_conclusao)),
        ganhoOrdem: item.data_de_conclusao ?? undefined,
        pedidoOrdem: item.numero_do_pedido ?? undefined,
      })),
    [itens, empresas.nomes],
  )

  /*
   * O `!isError` na frente importa: numa recusa `consulta.data` também é `undefined`, e sem ele o
   * erro seria lido como "ainda carregando" — a tela sumiria e quem tomou um `500` ficaria sem
   * saída. É a mesma fórmula de `ListaDeOriginados` e de `PainelDeHoje`.
   */
  const carregando = !consulta.isError && (consulta.isLoading || consulta.data === undefined)
  const vazio = !carregando && !consulta.isError && linhas.length === 0

  return (
    <div className="entregas">
      {/* O cartão do protótipo v2: o título e, à direita, a nota que diz o que a data faz. */}
      <section className="entregas__cartao" aria-label={TITULO_DO_CARTAO}>
      <div className="entregas__cartao-cabecalho">
        <h2 className="entregas__cartao-titulo">{TITULO_DO_CARTAO}</h2>
        <span className="entregas__cartao-nota">{NOTA_DO_CARTAO}</span>
      </div>
      <p className="entregas__ajuda">{AJUDA}</p>
      {consulta.isError ? (
        <div className="entregas__recusa">
          {/* Verbatim: a recusa do serviço é a que sabe explicar o caso. */}
          <p className="entregas__erro" role="alert">
            {mensagemDe(consulta.error)}
          </p>
          <button
            type="button"
            className="entregas__botao"
            onClick={() => {
              void consulta.refetch()
            }}
          >
            {TENTAR_DE_NOVO}
          </button>
        </div>
      ) : carregando ? (
        <p className="entregas__aviso" role="status">
          {CARREGANDO}
        </p>
      ) : vazio ? (
        <p className="entregas__aviso">{SEM_PENDENCIA}</p>
      ) : (
        <>
          {/* O aviso vem **junto** com as linhas: a lista chegou, e o que falhou foi só o nome. */}
          {empresas.falhou ? (
            <p className="entregas__aviso entregas__aviso--recusa" role="alert">
              {NOMES_NAO_VIERAM}
            </p>
          ) : null}
          {ehCelular ? (
            <ul className="entregas__cartoes" aria-label={NOME_DA_LISTA}>
              {linhas.map((linha) => (
                <CartaoDaEntrega key={linha.id} linha={linha} />
              ))}
            </ul>
          ) : (
            <TabelaDasEntregas linhas={linhas} />
          )}
        </>
      )}
      </section>
    </div>
  )
}
